import React, { PureComponent } from 'react';
import { ReferencedProductType } from '~/Api/HrPriceReferencing/MaxPrice';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { formatNumericHumanDate, parseNumericHumanDate } from '~/DateTime';
import { DebounceInput } from '~/Debounce';
import DatePickerInput from '~/UI/DatePickerInput/DatePickerInput';
import Icon from '~/UI/Icon/Icon';
import defaultInputStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import SaveActivityIndicator, { Status } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import Select from '~/UI/Select/Select';
import Tooltip from '~/UI/Tooltip/Tooltip';

import styles from './styles.less';

export interface Value {
	isValid: boolean | null;
	value: string;
}

export interface Values {
	basketVersion: string;
	packageSize: Value;
	productType: string;
	historicProductDataViewDate: {
		value: string;
		dateValue: Date | null;
		isValid: boolean | null;
	};
	amountOfSubstanceInUnit: Value;
}

interface Props {
	productTypes: ReferencedProductType[] | null;
	updateStatus: Status | null;
	values: Values;
	versions: ReferenceBasketVersion[] | null;
	onChange(values: Values): void;
	onSave(): void;
}

export default class ParametersPanel extends PureComponent<Props> {
	public render() {
		const { updateStatus } = this.props;

		return (
			<div className={styles.container} data-testid='parameters-panel'>
				<div className={styles.content}>
					<div className={styles.parameters}>
						<p className={styles.parametersDescription}>Maximum price calculation parameters:</p>

						{this.renderHistoricViewDateInput()}
						{this.renderVersionSelect()}
						{this.renderProductTypeSelect()}
						{this.renderReferencePackageSize()}
						{this.renderAmountOfSubstanceInUnit()}

						<SaveActivityIndicator
							hideAfterMilliseconds={3000}
							status={updateStatus}
							testId='parameters-panel-save-indicator'
						/>
					</div>
				</div>
			</div>
		);
	}

	private renderAmountOfSubstanceInUnit() {
		const { values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-package-size'>
				<span className={styles.parameterLabelText}>Amount of substance in unit:</span>
				<div className={styles.parameterInput}>
					<DebounceInput
						value={values.amountOfSubstanceInUnit.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({
								...values,
								amountOfSubstanceInUnit: { value, isValid: null },
							});
						}}
					>
						<Input
							id='reference-package-size'
							isInvalid={values.amountOfSubstanceInUnit.isValid === false}
							placeholder='1'
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderReferencePackageSize() {
		const { values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-amount-of-substance-in-unit'>
				<span className={styles.parameterLabelText}>Package size:</span>
				<div className={styles.parameterInput}>
					<DebounceInput
						value={values.packageSize.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, packageSize: { value, isValid: null } });
						}}
					>
						<Input
							id='reference-amount-of-substance-in-unit'
							isInvalid={values.packageSize.isValid === false}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderVersionSelect() {
		const { versions, values, onChange } = this.props;
		const currentVersion = versions?.find((version) => version.id === values.basketVersion) ?? null;

		const descriptionIcon = (
			<span className={styles.parameterIcon}>
				<Icon name='violet-question-mark' />
			</span>
		);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Reference basket valid:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={versions === null}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={values.basketVersion}
						onChange={(event) => {
							onChange({ ...values, basketVersion: event.target.value });
						}}
					>
						{versions !== null
							? versions.map((version) => (
									<option key={version.id} value={version.id}>
										Since {formatNumericHumanDate(version.sinceDate)}
									</option>
							  ))
							: null}
					</Select>

					{currentVersion !== null ? <Tooltip reference={descriptionIcon}>{currentVersion.description}</Tooltip> : null}
				</div>
			</label>
		);
	}

	private renderProductTypeSelect() {
		const { productTypes, values, onChange } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-product-type'>
				<span className={styles.parameterLabelText}>Method:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={!productTypes}
						id='reference-product-type'
						styles={{ select: styles.select }}
						value={values.productType}
						onChange={(event) => {
							onChange({ ...values, productType: event.target.value });
						}}
					>
						{productTypes
							? productTypes.map((productType) => (
									<option key={productType.id} value={productType.id}>
										{productType.title}
									</option>
							  ))
							: null}
					</Select>
				</div>
			</label>
		);
	}

	private renderHistoricViewDateInput() {
		const { values, onChange } = this.props;
		const yearAgo = new Date();
		yearAgo.setDate(yearAgo.getDate() - 365 * 10);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-historic-date'>
				<span className={styles.parameterLabelText}>Reference date:</span>
				<div className={styles.dateParameterInput}>
					<DatePickerInput
						clearable
						dateValue={values.historicProductDataViewDate.dateValue}
						disabledDays={{ after: new Date(), before: yearAgo }}
						isInvalid={values.historicProductDataViewDate.isValid === false}
						placeholder='Today'
						styles={defaultInputStyles}
						value={values.historicProductDataViewDate.value}
						onChange={(value) => {
							onChange({
								...values,
								historicProductDataViewDate: {
									value,
									dateValue: parseNumericHumanDate(value),
									isValid: null,
								},
							});
						}}
					/>
				</div>
			</label>
		);
	}
}
